import InfoOutlineIcon from '@mui/icons-material/InfoOutlined';
import { css, Step, StepLabel, Stepper, Tooltip, Typography } from '@mui/material';
import { atom, useAtomValue } from 'jotai';
import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';
import { GenericFallbacksWrapper } from '../../../../../components/Fallback/GenericFallbacksWrapper';
import { Loader } from '../../../../../components/Loader/Loader';
import { ROUTES } from '../../../../../constants/RouteNameMapping';
import { LoadingStatus } from '../../../../../types';
import { useAsync } from '../../../../../util/hook-utils';
import {
  KPISubmissionState,
  loadRecipientViewInitialData,
  RecipientViewInitialData,
  useKPIRequestRecipientActions,
} from './hooks/useKPIRequestRecipientActions';
import { KPINoPendingRequest } from './KPINoPendingRequest';
import { KPIRecipientViewHeader3 } from './KPIRecipientViewHeader3';
import { KPIRenderedTemplate } from './KPIRenderedTemplate';
import {
  Background,
  headerWrapperCSS,
  headerWrapperInnerCSS,
  kpiContentsInnerWrapperCss,
  kpiContentsWrapperCss,
  mainContentWrapperCss,
} from './KpiViewCommonStyles';

export function KPIRecipientView3() {
  const { companyId } = useParams<{ companyId: string }>();
  const { error, loading, data } = useAsync(
    useCallback(() => loadRecipientViewInitialData(Number(companyId)), [companyId])
  );

  switch (loading) {
    case LoadingStatus.loading:
      return <Loader />;

    case LoadingStatus.error:
      return <Typography>{error}</Typography>;

    case LoadingStatus.success:
    default:
      if (!data.company || data.requestsForCompany.length === 0) {
        return <KPINoPendingRequest />;
      }
      return <Layout initialData={data} />;
  }
}

type LayoutProps = { initialData: RecipientViewInitialData };

const kpiRequestWrapperCss = css`
  display: flex;
  flex-direction: column;
  background-color: #f9fafa;
  border-radius: 0.5rem;
  padding: 1rem;
  border: 2px solid #fff;
  margin-top: 1rem;
`;

function Layout(props: LayoutProps) {
  const navigate = useNavigate();
  const { company, requestsForCompany, firstRequestResponse, firstRequestTemplate } = props.initialData;
  const componentState = useMemo(
    () =>
      atom<KPISubmissionState>({
        allRequests: requestsForCompany,
        currentRequest: requestsForCompany[0],
        currentResponse: firstRequestResponse,
        currentRequestIndex: 0,
        currentTemplate: firstRequestTemplate,
        done: false,
        isLoadingData: false,
        isSubmitting: false,
        submissionError: null,
      }),
    [firstRequestResponse, firstRequestTemplate, requestsForCompany]
  );
  const { currentRequest, currentResponse, currentTemplate, done, isSubmitting, currentRequestIndex } =
    useAtomValue(componentState);
  const actions = useKPIRequestRecipientActions(componentState);

  useEffect(() => {
    if (done) {
      navigate(`/${ROUTES.KPI_THANK_YOU_SCREEN}`, { state: { companyId: company.id } });
    }
  }, [company.id, done, navigate]);

  const steps = useMemo(() => {
    return requestsForCompany.map((req, idx) => {
      return (
        <Step key={req.id} active={idx === currentRequestIndex} completed={idx < currentRequestIndex}>
          <StepLabel>Request {idx + 1}</StepLabel>
        </Step>
      );
    });
  }, [currentRequestIndex, requestsForCompany]);

  return (
    <Background key={currentRequest.id}>
      <div css={mainContentWrapperCss}>
        <div css={headerWrapperCSS}>
          <div css={headerWrapperInnerCSS}>
            <KPIRecipientViewHeader3
              kpiRequest={currentRequest}
              company={company}
              onSaveDraft={actions.onSaveDraft}
              onSubmitResponse={actions.submitKPIResponse}
              isSubmitting={isSubmitting}
            />
          </div>
        </div>
        <div css={kpiContentsWrapperCss}>
          {steps.length > 1 ? <Stepper sx={{ marginBottom: '1rem' }}>{steps}</Stepper> : <div />}
          {!done ? (
            <div css={kpiContentsInnerWrapperCss}>
              <GenericFallbacksWrapper>
                <div css={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography
                    color='text.primary'
                    css={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                  >
                    <b>Editors (respondents):</b>{' '}
                    <span css={{ display: 'flex', alignItems: 'center' }}>
                      {currentRequest.respondent.join(', ')}
                      <Tooltip
                        title={`The progress of the request is automatically saved. 
${currentRequest.respondent.join(', ')} can work on it and complete it at any time.`}
                      >
                        <InfoOutlineIcon
                          sx={{ fontSize: '1.2rem', marginLeft: '0.5rem', color: 'text.secondary' }}
                        />
                      </Tooltip>
                    </span>
                  </Typography>
                </div>
                <div css={kpiRequestWrapperCss}>
                  {currentTemplate ? (
                    <KPIRenderedTemplate
                      onKPIGridDataUpdated={actions.onKPIGridDataUpdated}
                      onSectionDataUpdated={actions.onSectionDataUpdated}
                      onTextualKpiUpdated={actions.onTextualKpiUpdated}
                      request={currentRequest}
                      response={currentResponse}
                      template={currentTemplate}
                    />
                  ) : (
                    <Typography>Invalid Template</Typography>
                  )}
                </div>
              </GenericFallbacksWrapper>
            </div>
          ) : null}
        </div>
      </div>
    </Background>
  );
}
